import React, { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';

const RuleImpactList = ({ data = [], loading }) => {
  const maxDelta = useMemo(() => Math.max(...data.map((r) => Math.abs(r.delta)), 1), [data]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-10 bg-muted rounded mb-2" />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-muted-foreground">Rule Impact Score</h3>
      </div>
      <p className="text-xs text-muted-foreground/60 mb-4">
        Ranked by delta: avg PnL when followed minus avg PnL when broken
      </p>

      {data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <TrendingUp className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">Need both followed & broken data per rule.</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Keep journaling to unlock impact scores.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.map((rule, idx) => {
            const barWidth = Math.round((Math.abs(rule.delta) / maxDelta) * 100);
            const isPositive = rule.delta >= 0;
            return (
              <div key={rule.tag} className="group">
                <div className="flex items-center gap-2 mb-1">
                  {/* Rank badge */}
                  <span
                    className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      idx < 3
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {idx + 1}
                  </span>

                  {/* Rule label */}
                  <span className="flex-1 text-xs font-medium text-foreground truncate" title={rule.label}>
                    {rule.label}
                  </span>

                  {/* Delta */}
                  <span
                    className={`shrink-0 text-xs font-semibold tabular-nums ${
                      isPositive ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {isPositive ? '+' : ''}${rule.delta.toFixed(2)}
                  </span>
                </div>

                {/* Impact bar */}
                <div className="ml-7 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isPositive ? 'bg-green-500/70' : 'bg-red-500/70'
                      }`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <span className="shrink-0 text-[10px] text-muted-foreground/60 tabular-nums">
                    {rule.sample_size} trades
                  </span>
                </div>

                {/* Sub-text: followed vs broken */}
                <div className="ml-7 flex gap-3 mt-0.5">
                  <span className="text-[10px] text-green-400/70">
                    ✓ ${rule.followed_avg_pnl.toFixed(2)}
                  </span>
                  <span className="text-[10px] text-red-400/70">
                    ✗ ${rule.broken_avg_pnl.toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RuleImpactList;
